import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface UseMobileSidebarAccordionProps {
  defaultExpanded?: boolean;
  persistKey?: string;
}

// Simple media query hook for lg breakpoint (1024px)
const useIsLargeScreen = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === 'undefined') return true; // SSR default
    return window.innerWidth >= 1024;
  });

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsLargeScreen(e.matches);
    };

    mql.addEventListener('change', handleChange);
    setIsLargeScreen(mql.matches);

    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  return isLargeScreen;
};

export const useMobileSidebarAccordion = ({
  defaultExpanded = false,
  persistKey
}: UseMobileSidebarAccordionProps = {}) => {
  const isLargeScreen = useIsLargeScreen();
  const location = useLocation();
  
  // Auto-opening is disabled - always use defaultExpanded
  const isOnPriorityRoute = false;
  
  // Always use defaultExpanded, ignore localStorage
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const toggle = () => setIsExpanded(prev => !prev);
  
  // On desktop (>= 1024px), always expanded (accordion behavior disabled)
  // On mobile or tablet (< 1024px), use accordion behavior
  const isBelowLg = !isLargeScreen;
  const effectiveExpanded = isBelowLg ? isExpanded : true;
  const showAccordion = isBelowLg;
  
  return {
    isExpanded: effectiveExpanded,
    toggle,
    showAccordion,
    isMobile: isBelowLg,
    isOnPriorityRoute
  };
};
